import { type FormEvent, useState } from "react"

import { cn } from "@/lib/utils"

/**
 * IMP-502: the website-training CTA, shared by the hero and the final CTA so
 * the two never drift. Shown to every partner, including partner-managed
 * ones: the assessment is free and happens before any purchase, so it is not
 * the self-serve checkout their setup hides. `tone="on-mesh"` darkens the
 * caption for legibility on the gradient panel.
 *
 * IMP-503 follow-up: submission awaits the server's pre-screen, so an address
 * we can't assess resolves right here with a neutral message and never opens
 * the overlay. The hidden "company" field is a honeypot real visitors never
 * see or fill.
 */
export function TrainForm({
  onTrain,
  tone = "on-page",
}: {
  /** Starts the assessment; resolves to null on success (the overlay opens)
   *  or a visitor-facing message to show under the field. */
  onTrain: (website: string, company: string) => Promise<string | null>
  tone?: "on-page" | "on-mesh"
}) {
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (pending) return
    const data = new FormData(event.currentTarget)
    const websiteValue = data.get("website")
    const companyValue = data.get("company")
    const website = typeof websiteValue === "string" ? websiteValue.trim() : ""
    const company = typeof companyValue === "string" ? companyValue : ""
    if (!website) return
    setPending(true)
    setError(null)
    void onTrain(website, company).then((message) => {
      setPending(false)
      setError(message)
    })
  }

  return (
    <>
      <form
        onSubmit={handleSubmit}
        className="relative flex w-[min(560px,100%)] flex-col gap-2.5 sm:flex-row"
      >
        <input
          type="text"
          name="website"
          inputMode="url"
          autoComplete="url"
          aria-label="Your website"
          placeholder="yourbusiness.com"
          className={cn(
            "text-lp-fg placeholder:text-lp-muted/60 focus:border-lp-fg h-12 min-w-0 flex-1 rounded-full border bg-white px-6 text-[15px] transition-colors outline-none",
            tone === "on-mesh" ? "border-white/40" : "border-[#d6d6d2]",
          )}
        />
        {/* Honeypot: invisible to real visitors and assistive tech, filled
            only by form-stuffing bots. The server answers a filled honeypot
            exactly like a success. */}
        <input
          type="text"
          name="company"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          className="absolute -left-[9999px] h-px w-px opacity-0"
        />
        <button
          type="submit"
          disabled={pending}
          className="bg-lp-fg h-12 shrink-0 rounded-full px-7 text-[15px] font-semibold text-white transition-colors select-none hover:bg-black disabled:opacity-60"
        >
          Meet your assistant
        </button>
      </form>
      <p
        className={cn(
          "mt-4 text-[13.5px]",
          error
            ? "text-lp-fg"
            : tone === "on-mesh"
              ? "text-lp-fg/70"
              : "text-lp-muted/70",
        )}
      >
        {error ??
          "Enter your website and hear how your assistant will answer your phone."}
      </p>
    </>
  )
}
